import React, { useState, useEffect, useContext } from "react";
import { useHistory } from "react-router-dom";

import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import {
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  ListItemText,
  makeStyles,
  MenuItem,
  Select,
  Typography,
  useTheme,
} from "@material-ui/core";
import { toast } from "react-toastify";
import CloseIcon from "../../assets/icon/CloseIcon";
import DownArrow from "../../assets/icon/DownArrow";
import CancelIcon from '@material-ui/icons/Cancel';
import { TicketsContext } from "../../context/Tickets/TicketsContextIndexedDB";

const useStyles = makeStyles((theme) => ({
  root: {
    "& .MuiDialog-paper": {
      backgroundColor: theme.palette.tabHeaderBackground,
      color: theme.palette.text.primary,
      borderRadius: 20
    },
  },
  dialogTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  closeButton: {
    color: theme.palette.text.primary,
  },
  content: {
    paddingTop: theme.spacing(2),
  },
  select: {
    "&.MuiOutlinedInput-root": {
      color: theme.palette.text.primary,
      "& fieldset": {
        borderColor: "rgba(255, 255, 255, 0.23)",
      },
      "&:hover fieldset": {
        borderColor: "rgba(255, 255, 255, 0.87)",
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
    "& .MuiInputLabel-outlined": {
      color: theme.palette.text.secondary,
    },
  },
  chip: {
    margin: theme.spacing(0.5),
    '& .MuiChip-deleteIcon': {
      color: 'inherit',
      '&:hover': {
        color: theme.palette.grey[100],
      },
    },
  },
  icon: {
    right: theme.spacing(1),
    position: "absolute",
    pointerEvents: "none",
  },
  menuItem: {
    padding: theme.spacing(1, 2),
    color: theme.palette.text.primary,
  },
  btnWrapper: {
    position: "relative",
    borderRadius: 10,
    padding: 10,
    width: 100,
  },
  ticketInfo: {
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(2),
    borderRadius: 8,
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
  },
  contactInfo: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  currentInfo: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
}));

const ReopenTicketModal = ({ open, onClose, ticket, onReopenTicket }) => {
  const classes = useStyles();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [selectedQueue, setSelectedQueue] = useState([]);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [connections, setConnections] = useState([]);
  const [allConnections, setAllConnections] = useState([]);
  const [availableQueues, setAvailableQueues] = useState([]);
  const { user } = useContext(AuthContext);
  const { 
    setCurrentTicket, 
    ticketsList,
    updateTicketInList,
    setTicketsList
  } = useContext(TicketsContext);

  const theme = useTheme();

  useEffect(() => {
    if (!open) {
      setSelectedQueue([]);
      setSelectedConnection(null);
      setConnections([]);
      setAllConnections([]);
      setAvailableQueues([]);
    } else if (ticket) {
      // Pre-seleccionar la conexión y departamento actuales del ticket
      setSelectedConnection(ticket.whatsappId);
      if (ticket.queueId) {
        setSelectedQueue([ticket.queueId]);
      }
    }

    // Cargar las conexiones activas al abrir el modal
    const loadConnections = async () => {
      try {
        const { data } = await api.get(`/whatsapp?session=0&companyId=${user.companyId}`);
        setAllConnections(data);
        
        // Mostrar TODAS las conexiones disponibles (widget y WhatsApp)
        setConnections(data);
        
        // Si el ticket tiene una conexión específica, verificar que esté disponible
        if (ticket?.whatsappId) {
          const currentConnection = data.find(conn => conn.id === ticket.whatsappId);
          if (currentConnection) {
            setSelectedConnection(ticket.whatsappId);
          } else if (data.length === 1) {
            // Si la conexión actual no está disponible pero hay otra, seleccionarla
            setSelectedConnection(data[0].id);
          }
        } else if (data.length === 1) {
          setSelectedConnection(data[0].id);
        }
      } catch (err) {
        toastError(err);
      }
    };

    if (open) {
      loadConnections();
    }
  }, [open, ticket, user.queues]);

  // Filtrar departamentos basándose en la conexión seleccionada
  useEffect(() => {
    const filterQueues = async () => {
      if (selectedConnection && allConnections.length > 0) {
        try {
          // Obtener la conexión seleccionada con sus departamentos
          const { data: connectionData } = await api.get(`/whatsapp/${selectedConnection}`);
          
          if (connectionData && connectionData.whatsappQueues) {
            let filteredQueues = connectionData.whatsappQueues;

            const userQueueIds = user.queues;
              filteredQueues = connectionData.whatsappQueues.filter(queue => 
              userQueueIds.includes(queue.queue.id)
            );
            console.log('filteredQueues', filteredQueues.map(q => q.queue))
            setAvailableQueues(filteredQueues.map(q => q.queue));
            
            // Auto-seleccionar si solo hay un departamento disponible
            if (filteredQueues.length === 1) {
              setSelectedQueue([filteredQueues[0].queue.id]);
            } else {
              // Verificar si el departamento actual del ticket está disponible
              if (ticket?.queueId) {
                const isCurrentQueueAvailable = filteredQueues.some(q => q.queue.id === ticket.queueId);
                if (isCurrentQueueAvailable) {
                  setSelectedQueue([ticket.queue.id]);
                } else {
                  setSelectedQueue([]);
                }
              }
            }
          } else {
            setAvailableQueues([]);
            setSelectedQueue([]);
          }
        } catch (err) {
          console.error("Error al obtener departamentos de la conexión:", err);
          setAvailableQueues([]);
          setSelectedQueue([]);
        }
      } else {
        // Si no hay conexión seleccionada, resetear departamentos
        setAvailableQueues([]);
        setSelectedQueue([]);
      }
    };

    filterQueues();
  }, [selectedConnection, allConnections, user.profile, user.queues, ticket]);

  const handleClose = () => {
    setSelectedQueue([]);
    setSelectedConnection(null);
    setConnections([]);
    setAllConnections([]);
    setAvailableQueues([]);
    onClose();
  };

  const handleReopenTicket = async () => {
    if (!ticket?.id) {
      toast.error("Error: No se encontró el ticket");
      return;
    }
    if (!selectedConnection) {
      toast.error("Seleccione una conexión");
      return;
    }
    
    setLoading(true);
    try {
      const queueId = selectedQueue.length > 0 ? selectedQueue[0] : null;
      
      // Actualizar el ticket con la nueva conexión y departamento
      const { data: updatedTicket } = await api.put(`/tickets/${ticket.id}`, {
        status: "open",
        userId: user.id,
        queueId,
        whatsappId: selectedConnection,
        // currentUserId: user.id // Para el tracking de cambios
      });

      // Llamar a la función onReopenTicket del componente padre
      if (onReopenTicket) {
        onReopenTicket(updatedTicket);
      }      
      setLoading(false);
      
      // Cerrar el modal después de la reapertura exitosa
      handleClose();
    } catch (err) {
      setLoading(false);
      toastError(err);
    }
  };

  const handleRemoveQueue = (event, queueId) => {
    event.preventDefault();
    event.stopPropagation();
    setSelectedQueue(prevQueues => prevQueues.filter(id => id !== queueId));
  };

  const getChannelIcon = (channel) => {
    switch (channel) {
      case 'whatsapp':
        return '📱 ';
      case 'facebook':
        return '📘 ';
      case 'instagram':
        return '📷 ';
      case 'webchat':
        return '💬 ';
      default:
        return '🔗 ';
    }
  };

  const getCurrentConnectionName = () => {
    if (!ticket?.whatsappId) return "Sin conexión";
    return ticket.whatsapp ? `${getChannelIcon(ticket.whatsapp.channel)}${ticket.whatsapp.name}` : "Conexión no encontrada";
  };

  const getCurrentQueueName = () => {
    if (!ticket?.queue) return "Sin departamento";
    return ticket.queue.name;
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      className={classes.root}
    >
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Typography variant="h6">Reabrir Ticket</Typography>
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={handleClose}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent className={classes.content}>
        {/* Información del ticket */}
        {ticket && (
          <div className={classes.ticketInfo}>
            <Typography variant="h6" gutterBottom>
              Información del Ticket
            </Typography>
            <div className={classes.contactInfo}>
              <Typography variant="body1">
                <strong>Contacto:</strong> {ticket.contact?.name || "Sin nombre"}
              </Typography>
              {ticket.contact?.number && (
                <Typography variant="body2" className={classes.currentInfo}>
                  ({ticket.contact.number})
                </Typography>
              )}
            </div>
            <Typography variant="body2" className={classes.currentInfo}>
              <strong>Conexión actual:</strong> {getCurrentConnectionName()}
            </Typography>
            <Typography variant="body2" className={classes.currentInfo}>
              <strong>Departamento anterior:</strong> {getCurrentQueueName()}
            </Typography>
          </div>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Seleccione una conexión</InputLabel>
              <Select
                label="Seleccione una conexión"
                fullWidth
                value={selectedConnection || ''}
                onChange={(e) => setSelectedConnection(e.target.value)}
                className={classes.select}
                IconComponent={() => (
                  <div className={classes.icon}>
                    <DownArrow fill={theme.palette.messageIcons} />
                  </div>
                )}
                MenuProps={{
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                  },
                  getContentAnchorEl: null,
                }}
              >
                {connections?.length > 0 ? (
                  connections.map((conn) => (
                    <MenuItem
                      key={conn.id}
                      value={conn.id}
                      className={classes.menuItem}
                    >
                      <ListItemText 
                        primary={`${getChannelIcon(conn.channel)}${conn.name}`}
                        secondary={`Canal: ${conn.channel}`}
                      />
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled className={classes.menuItem}>
                    <ListItemText primary="No hay conexiones disponibles" />
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Seleccione un departamento</InputLabel>
              <Select
                label="Seleccione un departamento"
                fullWidth
                value={selectedQueue}
                onChange={(e) => setSelectedQueue([e.target.value])}
                className={classes.select}
                renderValue={(selected) => (
                  <div>
                    {selected.map((value) => {
                      const queue = availableQueues.find((q) => q.id === value);
                      return (
                        <Chip
                          key={queue.id}
                          label={queue.name}
                          onDelete={(event) => handleRemoveQueue(event, queue.id)}
                          className={classes.chip}
                          style={{ backgroundColor: queue.color }}
                          deleteIcon={
                            <CancelIcon 
                              style={{ color: 'inherit' }}
                              onMouseDown={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                              }}
                            />
                          }
                        />
                      );
                    })}
                  </div>
                )}
                IconComponent={() => (
                  <div className={classes.icon}>
                    <DownArrow fill={theme.palette.messageIcons} />
                  </div>
                )}
                MenuProps={{
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                  },
                  getContentAnchorEl: null,
                }}
              >
                {availableQueues?.length > 0 ? (
                  availableQueues.map((queue) => (
                    <MenuItem
                      key={queue.id}
                      value={queue.id}
                      className={classes.menuItem}
                    >
                      <ListItemText primary={queue.name} />
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled className={classes.menuItem}>
                    <ListItemText primary={
                      selectedConnection 
                        ? "Sin departamentos disponibles para esta conexión" 
                        : "Seleccione primero una conexión"
                    } />
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          color="primary"
          disabled={loading}
          variant="outlined"
          className={classes.btnWrapper}
        >
          Cancelar
        </Button>
        <ButtonWithSpinner
          variant="containedPrimary"
          type="button"
          disabled={!selectedConnection || loading}
          onClick={handleReopenTicket}
          loading={loading}
          className={classes.btnWrapper}
          style={{ color: "black" }}
        >
          Reabrir
        </ButtonWithSpinner>
      </DialogActions>
    </Dialog>
  );
};

export default ReopenTicketModal;